import { useState, MouseEvent, useEffect } from 'react';
import { ChatProfile, Icon } from '~/components/atomic';
import { FullScreenLoading } from '~/components/common';
import ChatLeaveMessage from '~/components/common/ChatLeaveMessage/ChatLeaveMessage';
import {
  useLeaveChatRoomMutation,
  useLeaveGroupChatRoomMutation,
  useSetNotificationMutation,
} from '~/features/chatRoom';
import { useChatListSwipe } from '~/hooks';
import {
  ChatAllRoomType,
  GetChatRoomListData,
  LeaveChatRoomRequest,
  SetNotificationRequest,
} from '~/types';
import { formatSendTime } from '~/utils';
import { parseTextWithItalics } from '~/utils/parse';
import Detective from '~/assets/img/icons/detective.svg';
import Groups from '~/assets/img/icons/groups.svg';
import { useUserStore } from '~/store';
import * as S from './index.styled';

interface ListProps {
  id: string;
  title: string;
  characters: GetChatRoomListData['ai_thumbnails'];
  handleChangeUrl: (id: string) => void;
  chat: string;
  time: string;
  type: ChatAllRoomType;
  isOwner: boolean;
  totalMember: number;
  notificationOn: boolean;
  isOpenMenu: boolean;
  setOpenMenuId: (id: string) => void;
}

export default function ChatRoomListItem({
  id,
  title,
  characters,
  handleChangeUrl,
  chat,
  time,
  type,
  isOwner,
  totalMember,
  notificationOn,
  isOpenMenu,
  setOpenMenuId,
}: ListProps) {
  const email = useUserStore((state) => state.email);
  const [translateX, setTranslateX] = useState(0);
  const [showLeaveRoomMessage, setLeaveRoomMessage] = useState(false);
  const [isNotificationOn, setIsNotificationOn] = useState(notificationOn);
  const { mutate: leaveChatRoomMutate, isPending: isChatPending } =
    useLeaveChatRoomMutation();
  const { mutate: leaveGroupChatRoomMutate, isPending: isGroupChatPending } =
    useLeaveGroupChatRoomMutation();
  const { mutate: setNotificationMutate } = useSetNotificationMutation();

  const splitMsg = parseTextWithItalics(chat);

  const handleAlertClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    handleNotifyClick();
  };

  const handleDeleteClick = (
    e: MouseEvent<HTMLButtonElement> | MouseEvent<HTMLLIElement>,
  ) => {
    e.stopPropagation();
    setLeaveRoomMessage(true);
  };

  const handleNotifyClick = async () => {
    const newNotificationState = !isNotificationOn;
    const requestData: SetNotificationRequest = {
      chatroom_id: id,
      // room_type: type === 'group' ? 'group' : 'personal',
      notification: newNotificationState,
    };
    setNotificationMutate(requestData, {
      onSuccess({ status }) {
        setIsNotificationOn(newNotificationState);
      },
    });
  };

  const handleLeaveRoomClick = async (group: boolean) => {
    const requestData: LeaveChatRoomRequest = {
      chatroom_id: id,
    };
    if (!group) {
      leaveChatRoomMutate(requestData, {
        onSuccess: ({ status }) => {
          setLeaveRoomMessage(false);
        },
      });
    } else {
      leaveGroupChatRoomMutate(requestData, {
        onSuccess: ({ status }) => {
          setLeaveRoomMessage(false);
        },
      });
    }
  };

  const handleSwipe = (distance: number) => {
    const newTranslateX = Math.min(0, Math.max(-164, translateX + distance));
    setTranslateX(newTranslateX);
  };

  const handleSwipeEnd = () => {
    if (translateX < -120) {
      setTranslateX(-164);
      setOpenMenuId(id);
    } else {
      setTranslateX(0);
      setOpenMenuId('');
    }
  };

  const { handlers, isDragging } = useChatListSwipe({
    onSwipe: handleSwipe,
    onSwipeEnd: handleSwipeEnd,
    onTap: () => handleChangeUrl(id),
  });

  useEffect(() => {
    if (isOpenMenu) {
      setTranslateX(-164);
    } else {
      setTranslateX(0);
    }
  }, [isOpenMenu]);

  return (
    <>
      {(isChatPending || isGroupChatPending) && <FullScreenLoading />}

      <S.Container>
        <S.ListInfoWrapper
          className='list'
          translateX={translateX}
          isDragging={isDragging}
          {...handlers}
        >
          <S.ListInfoBox>
            <S.Info>
              <ChatProfile characters={characters} />

              <S.DetailBox>
                <S.NameInfoBox>
                  {(isOwner || type === 'group' || type === 'mystery') && (
                    <S.UsersInfoIconBox>
                      {isOwner && <Icon size={16} iconName='RoomManager' />}
                      {type === 'group' && (
                        <img src={Groups} alt='group_icon' />
                      )}
                      {type === 'mystery' && (
                        <img src={Detective} alt='mystery_icon' />
                      )}
                    </S.UsersInfoIconBox>
                  )}

                  <S.Name className={`${title ? '' : 'delete'}`}>
                    {title || 'Deleted Character'}
                  </S.Name>
                  {type === 'group' && (
                    <S.TotalUsersCount>{totalMember}</S.TotalUsersCount>
                  )}
                </S.NameInfoBox>
                <S.Message>
                  {splitMsg.map(({ italic, text }, index) => {
                    return (
                      <span className={italic ? 'italic' : ''} key={index}>
                        {text}
                      </span>
                    );
                  })}
                </S.Message>
              </S.DetailBox>
            </S.Info>
            <S.Time>
              <span>{formatSendTime(time)}</span>
            </S.Time>
          </S.ListInfoBox>
        </S.ListInfoWrapper>

        <S.BehindButtonWrapper>
          <S.ButtonBox>
            {/* here */}
            <S.Button className='notice' onClick={handleAlertClick}>
              <Icon
                size={28}
                iconName={
                  isNotificationOn ? 'Notifications' : 'NotificationOff'
                }
                color='content_dark_1'
              />
              <span>notice</span>
            </S.Button>
          </S.ButtonBox>
          <S.ButtonBox>
            <S.Button className='delete' onClick={handleDeleteClick}>
              <Icon size={28} iconName='Delete' color='content_dark_1' />
              <span>delete</span>
            </S.Button>
          </S.ButtonBox>
        </S.BehindButtonWrapper>
      </S.Container>

      <ChatLeaveMessage
        group={type === 'group'}
        isOpen={showLeaveRoomMessage}
        onClose={() => setLeaveRoomMessage(false)}
        handleRoomLeaveClick={() => handleLeaveRoomClick(type === 'group')}
      />
    </>
  );
}
